import { Theme, useTheme } from "@emotion/react";
import {
  Alert,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
} from "@mui/material";
import FieldRichText from "components/FieldRichText";
import { pick } from "lodash";
import { useMemo } from "react";
import { Form, FormProps } from "react-final-form";
import { useIntl } from "react-intl";
import {
  Attribute,
  AttributeMetaValue,
  OrganizationAttributeType,
  SaveAttributeRequest,
} from "services/KnowfullyAdminClient";
import { CommonFormProps } from "types/common-types";
import { isEmptyOrSpaces } from "utils/formUtils";
import {
  attributeTypeConfigs,
  getMetaValueComponent,
} from "./CustomAttributeComponents";
import Field from "components/Field";

const useStyles = (theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
});

type AttributeFormProps = {
  organizationAttributeType?: OrganizationAttributeType;
} & CommonFormProps<Attribute, SaveAttributeRequest> &
  FormProps<SaveAttributeRequest>;

export function AttributeForm({
  organizationAttributeType,
  attribute,
  submitLabel,
  onSubmit,
  onCancel,
}: AttributeFormProps) {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());

  const codeConstant =
    organizationAttributeType?.attributeType?.codeConstant?.toLowerCase();

  const attributeConfig = attributeTypeConfigs.find(
    (c) => c.codeConstant === codeConstant,
  );

  const init = useMemo(() => {
    const base = pick(attribute ?? {}, [
      "id",
      "value",
      "displayText",
      "sequence",
      "attributeTypeId",
    ]);

    if (!attributeConfig) {
      return {
        ...base,
        attributeMetaValues: attribute?.attributeMetaValues ?? [],
      };
    }

    // Fields bind by index (attributeMetaValues[index]); dedupe by name and
    // align with config order so duplicates from GET don't corrupt writes.
    const alignedMetaValues = attributeConfig.metaValues.map((metaValueName) => {
      const existing = attribute?.attributeMetaValues?.find(
        (m: AttributeMetaValue) => m.name === metaValueName,
      );
      return {
        id: existing?.id,
        attributeId: existing?.attributeId,
        name: metaValueName,
        value: existing?.value ?? "",
      };
    });

    return {
      ...base,
      attributeMetaValues: alignedMetaValues,
    };
  }, [attribute, attributeConfig]);

  const hasCustomMetaValues = !!attributeConfig;
  const showDisplayText =
    organizationAttributeType?.attributeType?.hasDisplayText !== false;
  const showSequence =
    organizationAttributeType?.attributeType?.hasSequence !== false;

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      render={({ handleSubmit, submitting, submitError, values }) => (
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              {hasCustomMetaValues ? (
                <Field name="value" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "nameLabel" })}
                        required
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>
              ) : (
                <Field name="value" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FieldRichText
                        input={input}
                        meta={meta}
                        label={t({ id: "valueLabel" })}
                        init={{
                          height: 280,
                        }}
                      />
                    </Grid>
                  )}
                </Field>
              )}

              {showDisplayText ? (
                <Field name="displayText" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        required
                        InputLabelProps={{ required: true }}
                        label={t({ id: "displayText" })}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>
              ) : (
                <Field name="displayText" initialValue={values.value}>
                  {() => null}
                </Field>
              )}

              {attributeConfig?.metaValues.map((metaValueName, index) => {
                const config = getMetaValueComponent(metaValueName);
                if (!config) return null;
                const { FormField } = config;
                return (
                  <FormField
                    key={metaValueName}
                    metaValueName={metaValueName}
                    index={index}
                    attributeMetaValues={values.attributeMetaValues}
                  />
                );
              })}

              {showSequence ? (
                <Field name="sequence">
                  {({ input, meta }) => (
                    <Grid item xs={6}>
                      <TextField
                        variant="standard"
                        fullWidth
                        type="number"
                        label={t({ id: "sequenceLabel" })}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>
              ) : (
                <Field name="sequence" initialValue={0}>
                  {() => null}
                </Field>
              )}
              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            {submitting && <CircularProgress size={24} />}
            <Button disabled={submitting} onClick={onCancel}>
              {t({ id: "cancelButton" })}
            </Button>

            <Button
              type="submit"
              color="primary"
              disabled={
                submitting ||
                (showDisplayText && isEmptyOrSpaces(values?.displayText!)) ||
                isEmptyOrSpaces(values.value)
              }
            >
              {submitLabel}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
}
